import { useState, useEffect, useCallback, useMemo } from "react";
import api, { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { format } from "date-fns";
import { Search, X, Filter, Upload, Plus, DollarSign, FileText, Calendar, Users, Menu, Copy, Clock3, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";

import { Sidebar } from "@/components/layout/Sidebar";
import { KanbanBoard } from "@/components/kanban/KanbanBoard";
import { ContactFormModal } from "@/components/modals/ContactFormModal";
import { ScheduleModal } from "@/components/modals/ScheduleModal";
import { ImportModal } from "@/components/modals/ImportModal";
import { ScriptModal } from "@/components/modals/ScriptModal";
import { NoteModal } from "@/components/modals/NoteModal";
import { PipelineFormModal } from "@/components/modals/PipelineFormModal";
import { PipelineValueModal } from "@/components/modals/PipelineValueModal";
import { DeletePipelineModal } from "@/components/modals/DeletePipelineModal";
import { ManageTeamModal } from "@/components/modals/ManageTeamModal";
import { DuplicatesModal } from "@/components/modals/DuplicatesModal";
import { SettingsModal } from "@/components/modals/SettingsModal";
import { MotivoPerdidoModal } from "@/components/modals/MotivoPerdidoModal";
import { WhatsAppAdvanceModal } from "@/components/modals/WhatsAppAdvanceModal";
import { AgendaComercial } from "@/components/views/AgendaComercial";
import { Relatorios } from "@/components/views/Relatorios";
import { ETAPAS, NEXT_STAGE, TIPO_LABEL, ETAPA_LABEL } from "@/lib/stages";

const formatBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0, maximumFractionDigits: 0 });

const emptyForm = { nome: "", telefone: "", instagram: "", dm_instagram: "", origem: "", tipo: "novo", responsavel: "", intencao: "", retornar_em: "", proxima_acao: "" };

export default function Dashboard() {
  const { user, refresh: refreshAuth } = useAuth();

  const [contacts, setContacts] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [stats, setStats] = useState({ total: 0, novos: 0, base: 0, agendados: 0, faturamento: 0 });
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState("");

  const [filterTipo, setFilterTipo] = useState("all");
  const [filterResp, setFilterResp] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyRetornos, setOnlyRetornos] = useState(false);
  const [retornosCount, setRetornosCount] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [historico, setHistorico] = useState([]);
  const [novoRegistro, setNovoRegistro] = useState({ fase: "m1", responsavel: "", texto: "" });
  const [saving, setSaving] = useState(false);

  const [scheduleContact, setScheduleContact] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleValue, setScheduleValue] = useState("");

  const [noteContact, setNoteContact] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const [showPipelineForm, setShowPipelineForm] = useState(false);
  const [pipelineForm, setPipelineForm] = useState({ nome: "", tipo: "novo", offer_value: "" });
  const [showPipelineEdit, setShowPipelineEdit] = useState(false);
  const [pipelineEditValue, setPipelineEditValue] = useState("");
  const [deletePipeline, setDeletePipeline] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const [showScript, setShowScript] = useState(false);
  const [scriptText, setScriptText] = useState("");
  const [savingScript, setSavingScript] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importEtapa, setImportEtapa] = useState("entrada");
  const [importTipo, setImportTipo] = useState("novo");
  const [importing, setImporting] = useState(false);

  const [showTeam, setShowTeam] = useState(false);
  const [newRespName, setNewRespName] = useState("");

  const [showSettings, setShowSettings] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState("kanban");

  const [showDuplicates, setShowDuplicates] = useState(false);

  // Motivo perdido
  const [motivoTarget, setMotivoTarget] = useState(null); // { contact, nextEtapa }

  // Whatsapp advance
  const [waData, setWaData] = useState(null); // { contact, texto, link, proximaEtapaLabel }

  const cardParadoDias = user?.card_parado_dias || 3;
  const intencoes = user?.intencoes || [];

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterTipo !== "all") params.append("tipo", filterTipo);
      if (filterResp !== "all") params.append("responsavel", filterResp);
      if (selectedPipeline) params.append("pipeline_id", selectedPipeline);
      const results = await Promise.allSettled([
        api.get(`/contacts?${params}`),
        api.get(`/responsaveis?${selectedPipeline ? `pipeline_id=${selectedPipeline}` : ""}`),
        api.get(`/stats?${params}`),
        api.get(`/contacts/retornos`),
      ]);
      if (results[0].status === "fulfilled") setContacts(results[0].value.data);
      if (results[1].status === "fulfilled") setResponsaveis(results[1].value.data);
      if (results[2].status === "fulfilled") setStats(results[2].value.data);
      if (results[3].status === "fulfilled") setRetornosCount(results[3].value.data.length);
    } catch (e) {
      toast.error(errorMessage(e, "Erro ao carregar"));
    }
  }, [filterTipo, filterResp, selectedPipeline]);

  const loadPipelines = useCallback(async () => {
    try {
      const res = await api.get(`/pipelines`);
      setPipelines(res.data);
      if (res.data.length > 0 && !selectedPipeline) setSelectedPipeline(res.data[0].id);
    } catch (e) {
      toast.error(errorMessage(e, "Erro ao carregar campanhas"));
    }
  }, [selectedPipeline]);

  useEffect(() => { loadPipelines(); }, [loadPipelines]);
  useEffect(() => { if (selectedPipeline) load(); }, [load, selectedPipeline]);

  const currentPipeline = pipelines.find((p) => p.id === selectedPipeline);
  const offerValue = currentPipeline?.offer_value || 0;
  const totalLeads = contacts.length;
  const convertedLeads = contacts.filter((c) => c.etapa === "agendado" || c.convertido === true).length;
  const potencial = totalLeads * offerValue;
  const realizado = convertedLeads * offerValue;
  const parado = potencial - realizado;

  const contactCounts = useMemo(() => {
    const counts = {};
    pipelines.forEach((p) => { counts[p.id] = 0; });
    contacts.forEach((c) => { if (counts[c.pipeline_id] !== undefined) counts[c.pipeline_id]++; });
    return counts;
  }, [pipelines, contacts]);

  const filteredContacts = useMemo(() => {
    let list = contacts;
    if (onlyRetornos) {
      let today = "";
      try { today = new Date().toISOString().slice(0, 10); } catch { today = ""; }
      list = list.filter((c) => c.retornar_em && c.retornar_em <= today && !["agendado", "perdido"].includes(c.etapa));
    }
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().replace(/[\s\-()]/g, "");
    return list.filter((c) => {
      const nome = (c.nome || "").toLowerCase();
      const tel = (c.telefone || "").replace(/[\s\-()]/g, "");
      return nome.includes(q) || tel.includes(q);
    });
  }, [contacts, searchQuery, onlyRetornos]);

  // === HANDLERS ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefone) return toast.error("Nome e telefone obrigatórios");
    if (saving) return;
    setSaving(true);
    try {
      if (editContact) {
        await api.put(`/contacts/${editContact.id}`, form);
        toast.success("Atualizado");
      } else {
        await api.post(`/contacts`, { ...form, pipeline_id: selectedPipeline });
        toast.success("Criado");
      }
      setShowForm(false);
      setEditContact(null);
      setHistorico([]);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erro"));
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (contact, etapa) => {
    if (etapa === "agendado") { setScheduleContact(contact); return; }
    if (etapa === "perdido") { setMotivoTarget({ contact, nextEtapa: etapa }); return; }
    try {
      await api.post(`/contacts/${contact.id}/move`, { etapa });
      toast.success(`Movido para ${ETAPA_LABEL[etapa] || etapa}`);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erro"));
    }
  };

  const confirmMotivo = async (motivo) => {
    if (!motivoTarget) return;
    const { contact, nextEtapa } = motivoTarget;
    try {
      await api.post(`/contacts/${contact.id}/move`, { etapa: nextEtapa, motivo_perdido: motivo });
      toast.success("Movido para não convertido");
      setMotivoTarget(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erro"));
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleDate || !scheduleContact) return;
    try {
      await api.post(`/contacts/${scheduleContact.id}/schedule`, {
        data: format(scheduleDate, "yyyy-MM-dd"),
        valor: parseFloat(scheduleValue) || 0,
      });
      toast.success("Agendado");
      setScheduleContact(null); setScheduleDate(null); setScheduleValue("");
      load();
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir?")) return;
    try { await api.delete(`/contacts/${id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const openEdit = async (c) => {
    setForm({
      nome: c.nome, telefone: c.telefone, instagram: c.instagram,
      dm_instagram: c.dm_instagram || "", origem: c.origem, tipo: c.tipo,
      responsavel: c.responsavel, intencao: c.intencao || "",
      retornar_em: c.retornar_em || "", proxima_acao: c.proxima_acao || "",
    });
    setEditContact(c);
    setNovoRegistro({ fase: c.etapa || "m1", responsavel: c.responsavel || "", texto: "" });
    setShowForm(true);
    try {
      const res = await api.get(`/contacts/${c.id}`);
      setEditContact(res.data);
      setHistorico(Array.isArray(res.data.historico_atendimento) ? res.data.historico_atendimento : []);
    } catch { setHistorico([]); }
  };

  const handleToggleOptOut = async (checked) => {
    if (!editContact) return;
    try {
      const res = await api.put(`/contacts/${editContact.id}`, { opt_out: checked });
      setEditContact(res.data);
      toast.success(checked ? "Contato marcado como opt-out" : "Opt-out removido");
      load();
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const handleAddHistorico = async () => {
    if (!novoRegistro.texto.trim() || !editContact) return toast.error("Digite o texto do registro");
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.post(`/contacts/${editContact.id}/historico`, novoRegistro);
      const updatedHistorico = Array.isArray(res.data?.historico_atendimento) ? res.data.historico_atendimento : [...historico];
      setHistorico(updatedHistorico);
      setContacts((prev) => prev.map((c) => c.id === editContact.id ? { ...c, ultima_atividade: new Date().toISOString() } : c));
      setNovoRegistro({ fase: novoRegistro.fase, responsavel: novoRegistro.responsavel, texto: "" });
      toast.success("Registro adicionado");
    } catch (e) { toast.error(errorMessage(e, "Erro ao adicionar registro")); }
    finally { setSaving(false); }
  };

  const handleSaveNote = async () => {
    if (!noteContact || savingNote) return;
    setSavingNote(true);
    try {
      await api.put(`/contacts/${noteContact.id}/anotacao`, { texto: noteText });
      setContacts((prev) => prev.map((c) => c.id === noteContact.id ? { ...c, anotacao: noteText, ultima_atividade: new Date().toISOString() } : c));
      setNoteContact(null);
      toast.success("Anotação salva");
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
    finally { setSavingNote(false); }
  };

  const openWhatsApp = async (c) => {
    if (c.opt_out) { toast.error("Contato marcou não quer mais contato"); return; }
    if (c.whatsapp_valid === false) { toast.error("Telefone inválido"); return; }
    try {
      const { data } = await api.get(`/contacts/${c.id}/wa-message`);
      const proximaEtapaLabel = ETAPAS.find((e) => e.id === NEXT_STAGE[c.etapa])?.label;
      setWaData({ contact: c, texto: data.texto, link: data.link, proximaEtapaLabel });
    } catch (e) {
      toast.error(errorMessage(e, "Não foi possível preparar a mensagem"));
    }
  };

  const confirmWaAdvance = async () => {
    if (!waData) return;
    try {
      await api.post(`/contacts/${waData.contact.id}/wa-advance`, {});
      toast.success("Card avançado");
      setWaData(null);
      load();
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const handleCreatePipeline = async (e) => {
    e.preventDefault();
    if (!pipelineForm.nome.trim()) return toast.error("Nome obrigatório");
    try {
      const payload = { nome: pipelineForm.nome, tipo: pipelineForm.tipo, offer_value: parseFloat(pipelineForm.offer_value) || 0 };
      const res = await api.post(`/pipelines`, payload);
      setPipelines((prev) => [...prev, res.data]);
      setSelectedPipeline(res.data.id);
      setShowPipelineForm(false);
      setPipelineForm({ nome: "", tipo: "novo", offer_value: "" });
      toast.success("Campanha criada");
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const handleUpdatePipelineValue = async (e) => {
    e.preventDefault();
    if (!selectedPipeline) return;
    try {
      const res = await api.put(`/pipelines/${selectedPipeline}`, { offer_value: parseFloat(pipelineEditValue) || 0 });
      setPipelines((prev) => prev.map((p) => p.id === selectedPipeline ? res.data : p));
      setShowPipelineEdit(false);
      toast.success("Valor atualizado");
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const handleDeletePipeline = async () => {
    if (!deletePipeline) return;
    try {
      const res = await api.post(`/pipelines/${deletePipeline.id}/excluir`, { confirmacao_nome: deleteConfirmText });
      toast.success(`Campanha excluída${res.data.leads_ocultos > 0 ? ` (${res.data.leads_ocultos} leads ocultos)` : ""}`);
      const deletedId = deletePipeline.id;
      setDeletePipeline(null); setDeleteConfirmText("");
      const pRes = await api.get(`/pipelines`);
      setPipelines(pRes.data);
      if (selectedPipeline === deletedId || !pRes.data.some((p) => p.id === selectedPipeline)) {
        if (pRes.data.length > 0) setSelectedPipeline(pRes.data[0].id);
        else { setSelectedPipeline(""); setContacts([]); }
      }
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const handleSaveScript = async () => {
    if (!selectedPipeline) return;
    setSavingScript(true);
    try {
      const res = await api.put(`/pipelines/${selectedPipeline}`, { sales_script: scriptText });
      setPipelines((prev) => prev.map((p) => p.id === selectedPipeline ? res.data : p));
      toast.success("Script salvo");
      setShowScript(false);
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
    finally { setSavingScript(false); }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return toast.error("Selecione um arquivo");
    if (!selectedPipeline) return toast.error("Selecione uma campanha");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("pipeline_id", selectedPipeline);
      fd.append("etapa", importEtapa);
      fd.append("tipo", importTipo);
      const res = await api.post(`/contacts/import`, fd);
      toast.success(`${res.data.imported} leads importados`);
      setShowImport(false); setImportFile(null); setImportEtapa("entrada"); setImportTipo("novo");
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erro ao importar"));
    } finally { setImporting(false); }
  };

  const handleAddResponsavel = async () => {
    const nome = newRespName.trim();
    if (!nome) return;
    try {
      await api.post(`/responsaveis`, { nome });
      setResponsaveis((prev) => [...new Set([...prev, nome])].sort());
      setNewRespName("");
      toast.success(`${nome} adicionado(a)`);
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const handleRemoveResponsavel = async (nome) => {
    try {
      await api.delete(`/responsaveis/${encodeURIComponent(nome)}`);
      setResponsaveis((prev) => prev.filter((r) => r !== nome));
      toast.success(`${nome} removido(a)`);
    } catch (e) { toast.error(errorMessage(e, "Erro")); }
  };

  const sidebarContent = (
    <Sidebar
      pipelines={pipelines}
      selectedPipeline={selectedPipeline}
      onSelectPipeline={(id) => { setSelectedPipeline(id); setSidebarOpen(false); }}
      onNewPipeline={() => setShowPipelineForm(true)}
      onDeletePipeline={setDeletePipeline}
      onManageTeam={() => setShowTeam(true)}
      onOpenSettings={() => setShowSettings(true)}
      contactCounts={contactCounts}
      currentView={currentView}
      onChangeView={(v) => { setCurrentView(v); setSidebarOpen(false); }}
    />
  );

  return (
    <div className="flex h-screen bg-[#09090B] overflow-hidden">
      <div className="hidden md:block">{sidebarContent}</div>
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-[#18181B] border-zinc-800">{sidebarContent}</SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col overflow-hidden">
        {currentView === "kanban" && (
          <>
            <header className="flex-shrink-0 border-b border-zinc-800/60 px-6 py-5">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <button data-testid="mobile-menu-btn" onClick={() => setSidebarOpen(true)} className="md:hidden text-zinc-400 hover:text-zinc-200 transition-colors">
                    <Menu className="h-5 w-5" />
                  </button>
                  <div>
                    <h2 className="text-lg font-semibold text-zinc-50 tracking-tight">{currentPipeline?.nome || "Selecione uma campanha"}</h2>
                    {currentPipeline && (
                      <span className="text-[10px] uppercase tracking-wider text-zinc-600">
                        {currentPipeline.tipo === "novo" ? TIPO_LABEL.novo : TIPO_LABEL.base}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2.5 flex-wrap justify-end">
                  <Button
                    data-testid="retornos-btn"
                    onClick={() => setOnlyRetornos((v) => !v)}
                    size="sm"
                    className={`h-8 text-xs ${onlyRetornos ? "bg-amber-500 text-zinc-950 hover:bg-amber-600" : "bg-zinc-800/60 hover:bg-zinc-700 text-zinc-300"}`}
                  >
                    <Clock3 className="h-3.5 w-3.5 mr-1.5" />
                    {onlyRetornos ? "Mostrar todos" : `Retornos de hoje (${retornosCount})`}
                  </Button>
                  <Button data-testid="script-btn" onClick={() => { setScriptText(currentPipeline?.sales_script || ""); setShowScript(true); }} size="sm" className="bg-zinc-800/60 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 h-8 text-xs">
                    <FileText className="h-3.5 w-3.5 mr-1.5" /> Script
                  </Button>
                  <Button data-testid="edit-pipeline-value-btn" onClick={() => { setPipelineEditValue(currentPipeline?.offer_value || ""); setShowPipelineEdit(true); }} size="sm" className="bg-zinc-800/60 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 h-8 text-xs">
                    <DollarSign className="h-3.5 w-3.5 mr-1.5" />
                    {offerValue > 0 ? `Valor da oferta · R$${offerValue}` : "Valor da oferta"}
                  </Button>
                  <Button data-testid="import-leads-btn" onClick={() => setShowImport(true)} size="sm" className="bg-zinc-800/60 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 h-8 text-xs">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> Importar
                  </Button>
                  <Button data-testid="duplicates-btn" onClick={() => setShowDuplicates(true)} size="sm" className="bg-zinc-800/60 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 h-8 text-xs">
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Duplicados
                  </Button>
                  <Button data-testid="header-delete-pipeline-btn" onClick={() => { setDeleteConfirmText(""); setDeletePipeline(currentPipeline); }} disabled={!currentPipeline || pipelines.length <= 1} title={pipelines.length <= 1 ? "Não é possível excluir a última campanha" : "Excluir campanha"} size="sm" className="bg-zinc-800/60 hover:bg-red-50 text-zinc-400 hover:text-red-600 h-8 text-xs">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Excluir campanha
                  </Button>
                  <Button
                    data-testid="new-contact-btn"
                    onClick={() => { setEditContact(null); setForm(emptyForm); setShowForm(true); }}
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-zinc-950 h-8 text-xs font-semibold"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Novo lead
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-8 mb-5 flex-wrap">
                <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5 text-zinc-600" /><span className="text-sm font-semibold text-zinc-100 tabular-nums">{stats.total}</span><span className="text-xs text-zinc-500">leads</span></div>
                <div className="flex items-center gap-2"><span className="text-sm font-semibold text-zinc-100 tabular-nums">{stats.novos}</span><span className="text-xs text-zinc-500">cliente nova</span></div>
                <div className="flex items-center gap-2"><span className="text-sm font-semibold text-zinc-100 tabular-nums">{stats.base}</span><span className="text-xs text-zinc-500">cliente da base</span></div>
                <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-zinc-600" /><span className="text-sm font-semibold text-zinc-100 tabular-nums">{stats.agendados}</span><span className="text-xs text-zinc-500">agendados</span></div>
                <div className="flex items-center gap-2"><DollarSign className="h-3.5 w-3.5 text-zinc-600" /><span className="text-sm font-semibold text-zinc-100 tabular-nums">R${stats.faturamento}</span></div>
              </div>

              {offerValue > 0 && (
                <div className="flex items-center gap-8 mb-5 flex-wrap" data-testid="financial-panel">
                  <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-green-500" /><span className="text-xs text-zinc-500">Potencial</span><span className="text-sm font-semibold text-green-400 tabular-nums" data-testid="fin-potencial">{formatBRL(potencial)}</span></div>
                  <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-amber-500" /><span className="text-xs text-zinc-500">Parado</span><span className="text-sm font-semibold text-amber-400 tabular-nums" data-testid="fin-parado">{formatBRL(parado)}</span></div>
                  <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-blue-500" /><span className="text-xs text-zinc-500">Realizado</span><span className="text-sm font-semibold text-blue-400 tabular-nums" data-testid="fin-realizado">{formatBRL(realizado)}</span></div>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-xs min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-600" />
                  <input data-testid="search-input" type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Buscar por nome ou telefone..." className="w-full h-8 pl-9 pr-8 text-xs rounded-lg bg-transparent border border-zinc-800/60 text-zinc-100 placeholder-zinc-600 outline-none focus:border-amber-500/40 focus:ring-1 focus:ring-amber-500/20 transition-all" />
                  {searchQuery && (<button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-300"><X className="h-3.5 w-3.5" /></button>)}
                </div>

                <Filter className="h-3.5 w-3.5 text-zinc-600" />
                <Select value={filterTipo} onValueChange={setFilterTipo}>
                  <SelectTrigger data-testid="filter-tipo" className="h-8 w-36 text-xs bg-transparent border-zinc-800 text-zinc-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">Tipo</SelectItem>
                    <SelectItem value="novo" className="text-xs">{TIPO_LABEL.novo}</SelectItem>
                    <SelectItem value="base" className="text-xs">{TIPO_LABEL.base}</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterResp} onValueChange={setFilterResp}>
                  <SelectTrigger data-testid="filter-responsavel" className="h-8 w-36 text-xs bg-transparent border-zinc-800 text-zinc-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">Responsável</SelectItem>
                    {responsaveis.map((r) => (<SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </header>

            <main className="flex-1 overflow-hidden pt-6">
              {(searchQuery || onlyRetornos) && filteredContacts.length === 0 && (
                <div className="text-center py-16 text-zinc-600 text-sm" data-testid="search-empty">
                  Nenhum lead encontrado{searchQuery ? ` para "${searchQuery}"` : ""}.
                </div>
              )}
              <KanbanBoard
                contacts={filteredContacts}
                onEdit={openEdit}
                onWhatsApp={openWhatsApp}
                onNote={(c) => { setNoteContact(c); setNoteText(c.anotacao || ""); }}
                onMove={handleMove}
                onSchedule={setScheduleContact}
                onDelete={handleDelete}
                cardParadoDias={cardParadoDias}
                activeCardId={editContact?.id || noteContact?.id || scheduleContact?.id || motivoTarget?.contact?.id || waData?.contact?.id || null}
              />
            </main>
          </>
        )}

        {currentView === "agenda" && <AgendaComercial />}
        {currentView === "relatorios" && <Relatorios />}
      </div>

      <ContactFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditContact(null); setHistorico([]); }}
        form={form} setForm={setForm}
        onSubmit={handleSubmit} saving={saving}
        isEdit={!!editContact} editContact={editContact}
        historico={historico}
        novoRegistro={novoRegistro} setNovoRegistro={setNovoRegistro}
        onAddHistorico={handleAddHistorico}
        responsaveis={responsaveis}
        intencoes={intencoes}
        onToggleOptOut={handleToggleOptOut}
      />

      <ScheduleModal
        contact={scheduleContact}
        onClose={() => { setScheduleContact(null); setScheduleDate(null); setScheduleValue(""); }}
        scheduleDate={scheduleDate} setScheduleDate={setScheduleDate}
        scheduleValue={scheduleValue} setScheduleValue={setScheduleValue}
        onSubmit={handleSchedule}
      />

      <ImportModal
        open={showImport}
        onClose={() => { setShowImport(false); setImportFile(null); }}
        importFile={importFile} setImportFile={setImportFile}
        importEtapa={importEtapa} setImportEtapa={setImportEtapa}
        importTipo={importTipo} setImportTipo={setImportTipo}
        importing={importing} onSubmit={handleImport}
        pipelineId={selectedPipeline}
        pipelineName={currentPipeline?.nome}
      />

      <ScriptModal
        open={showScript}
        onClose={setShowScript}
        scriptText={scriptText} setScriptText={setScriptText}
        onSave={handleSaveScript} saving={savingScript}
        pipelineName={currentPipeline?.nome}
      />

      <NoteModal
        contact={noteContact}
        onClose={() => { setNoteContact(null); setNoteText(""); }}
        noteText={noteText} setNoteText={setNoteText}
        onSave={handleSaveNote} saving={savingNote}
      />

      <PipelineFormModal
        open={showPipelineForm}
        onClose={() => { setShowPipelineForm(false); setPipelineForm({ nome: "", tipo: "novo", offer_value: "" }); }}
        form={pipelineForm} setForm={setPipelineForm} onSubmit={handleCreatePipeline}
      />

      <PipelineValueModal
        open={showPipelineEdit}
        onClose={() => setShowPipelineEdit(false)}
        value={pipelineEditValue} setValue={setPipelineEditValue}
        onSubmit={handleUpdatePipelineValue}
        pipelineName={currentPipeline?.nome} totalLeads={totalLeads} formatBRL={formatBRL}
      />

      <DeletePipelineModal
        pipeline={deletePipeline}
        onClose={() => { setDeletePipeline(null); setDeleteConfirmText(""); }}
        confirmText={deleteConfirmText} setConfirmText={setDeleteConfirmText}
        onDelete={handleDeletePipeline} contacts={contacts}
      />

      <ManageTeamModal
        open={showTeam}
        onClose={() => { setShowTeam(false); setNewRespName(""); }}
        responsaveis={responsaveis}
        newName={newRespName} setNewName={setNewRespName}
        onAdd={handleAddResponsavel} onRemove={handleRemoveResponsavel}
      />

      <DuplicatesModal
        open={showDuplicates}
        onClose={() => setShowDuplicates(false)}
        pipelineId={selectedPipeline}
        pipelineName={currentPipeline?.nome}
        onRefresh={load}
      />

      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        onSaved={() => refreshAuth()}
      />

      <MotivoPerdidoModal
        open={!!motivoTarget}
        contactName={motivoTarget?.contact?.nome}
        onClose={() => setMotivoTarget(null)}
        onConfirm={confirmMotivo}
      />

      <WhatsAppAdvanceModal
        open={!!waData}
        data={waData}
        onClose={() => setWaData(null)}
        onConfirm={confirmWaAdvance}
      />
    </div>
  );
}
